// BackendPageForPrivacy.js — WorksForYou Privacy Policy.
//
// This is a starter draft written for an EU-based (Italian) sole-proprietor
// consultancy with a brochure-style website + email contact. It covers the
// GDPR essentials. Have a lawyer review before going live in your jurisdiction.

import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { ConfigContext } from '../../App';
import { Navigation, Footer } from '../../myReactLib/MainBlocks';

const LAST_UPDATED = 'June 12, 2026';

function BackendPageForPrivacy() {
  const { config, homepageData, contactData } = useContext(ConfigContext);
  if (!homepageData) return null;

  return (
    <div className="w4u-page">
      <Navigation menuBar={homepageData.menu_bar} prefix={config.PREFIX} />
      <main className="w4u-main">
        <article className="w4u-legal">
          <header className="w4u-legal__header">
            <h1 className="w4u-legal__title">Privacy Policy</h1>
            <p className="w4u-legal__date">Last updated: {LAST_UPDATED}</p>
          </header>

          <section className="w4u-legal__section">
            <h2>1. Who we are</h2>
            <p>
              This Privacy Policy describes how WorksForYou ("we", "us", "our") — a sole proprietorship
              operated by Eugenio Ruggeri based in Bologna, Italy — collects, uses, and protects your
              personal data when you visit this website (the "Site") or contact us about our services.
            </p>
            <p>
              We act as the data controller under the EU General Data Protection Regulation (GDPR).
              You can reach us anytime at <a href="mailto:worksforyou.solutions@gmail.com" className="w4u-legal__link">worksforyou.solutions@gmail.com</a>.
            </p>
          </section>

          <section className="w4u-legal__section">
            <h2>2. What we collect</h2>
            <p>
              <strong>When you browse the Site:</strong> our hosting provider records standard server logs
              (IP address, user-agent, timestamp, requested URL) for the purpose of operating and securing
              the Site. These logs are not used to build a profile of you.
            </p>
            <p>
              <strong>When you contact us:</strong> if you send us an email or fill in a contact form, we
              receive the information you provide — typically your name, email address, and the contents
              of your message — so we can reply.
            </p>
            <p>
              <strong>Cookies and analytics:</strong> the Site does not currently use cookies or third-party
              analytics. If we add them in the future, we will update this policy and request your consent
              where required by law.
            </p>
          </section>

          <section className="w4u-legal__section">
            <h2>3. Data from connected platforms (Meta)</h2>
            <p>
              Some of the tools we build for clients connect to <strong>Facebook and Instagram</strong>{' '}
              through Meta's official APIs (e.g. for social-media analytics). These tools access data —
              such as profile information, posts and media metadata, comments, and account/page
              insights — only after the account owner explicitly authorizes the connection through
              Meta's login flow, and only within the permissions granted there.
            </p>
            <p>
              We use this data exclusively to provide the agreed service to that client. We do not sell
              it, share it with third parties, or use it for advertising. Access can be revoked at any
              time from the Facebook/Instagram app settings, and you can request the deletion of any
              stored platform data at any time — see our{' '}
              <Link to="/data-deletion" className="w4u-legal__link">Data Deletion Instructions</Link>.
            </p>
          </section>

          <section className="w4u-legal__section">
            <h2>4. How we use your data</h2>
            <p>
              We use the personal data you share with us only to respond to your enquiries, scope potential
              projects, communicate about our services, and deliver the services a client engaged us for.
              We do not use your data for marketing or for automated decision-making, and we do not sell
              or rent your data to anyone.
            </p>
          </section>

          <section className="w4u-legal__section">
            <h2>5. Legal basis (GDPR Art. 6)</h2>
            <p>
              We process your data on one of these bases:
            </p>
            <ul>
              <li><strong>Pre-contractual / contractual necessity</strong> (Art. 6(1)(b)) when you reach out
                  about a potential engagement.</li>
              <li><strong>Legitimate interest</strong> (Art. 6(1)(f)) to maintain and secure the Site.</li>
              <li><strong>Consent</strong> (Art. 6(1)(a)) for anything that requires it (e.g., future
                  newsletters or non-essential cookies). You can withdraw consent at any time.</li>
            </ul>
          </section>

          <section className="w4u-legal__section">
            <h2>6. Sharing and processors</h2>
            <p>
              We may use third-party service providers ("processors") to operate the Site — for example,
              email and calendar services from Google Workspace, and our website hosting provider — bound
              by appropriate data-processing terms. We do not share your data with third parties beyond
              what is needed to provide those services or to comply with the law.
            </p>
          </section>

          <section className="w4u-legal__section">
            <h2>7. Data retention</h2>
            <p>
              We keep email correspondence only as long as needed to handle the conversation and any
              follow-up engagement, and to meet legal obligations (e.g. invoicing records). Data from
              connected platforms is kept only while the related service engagement is active. Server
              logs are typically rotated within a short period and then discarded.
            </p>
          </section>

          <section className="w4u-legal__section">
            <h2>8. Your rights</h2>
            <p>
              Under GDPR you have the right to:
            </p>
            <ul>
              <li>access the personal data we hold about you;</li>
              <li>have inaccurate data corrected;</li>
              <li>have your data deleted ("right to be forgotten");</li>
              <li>restrict or object to certain processing;</li>
              <li>receive your data in a portable form;</li>
              <li>lodge a complaint with your local supervisory authority (in Italy: the Garante per la
                protezione dei dati personali).</li>
            </ul>
            <p>
              To exercise any of these rights, email us at <a href="mailto:worksforyou.solutions@gmail.com" className="w4u-legal__link">worksforyou.solutions@gmail.com</a> (for deletion, see the step-by-step{' '}
              <Link to="/data-deletion" className="w4u-legal__link">Data Deletion Instructions</Link>). We respond within 30 days.
            </p>
          </section>

          <section className="w4u-legal__section">
            <h2>9. Security</h2>
            <p>
              We apply reasonable technical and organisational measures to protect your personal data
              against accidental loss, unauthorised access, and misuse. No method of transmission over
              the internet is 100% secure, but we use industry-standard practices.
            </p>
          </section>

          <section className="w4u-legal__section">
            <h2>10. International transfers</h2>
            <p>
              Some of our processors (e.g., Google Workspace) may transfer data outside the EU/EEA. Where
              they do, they rely on safeguards approved under GDPR, such as the European Commission's
              Standard Contractual Clauses.
            </p>
          </section>

          <section className="w4u-legal__section">
            <h2>11. Changes to this policy</h2>
            <p>
              We may update this policy from time to time. The most recent revision date is shown at the
              top. If we make material changes, we will indicate them clearly.
            </p>
          </section>

          <section className="w4u-legal__section">
            <h2>Contact</h2>
            <p>
              Questions? Email <a href="mailto:worksforyou.solutions@gmail.com" className="w4u-legal__link">worksforyou.solutions@gmail.com</a>.
            </p>
          </section>
        </article>
      </main>
      <Footer menuBar={homepageData.menu_bar} config={config} contactData={contactData} footer={homepageData.footer} />
    </div>
  );
}

export default BackendPageForPrivacy;
